/**
 * NOTAS FISCAIS — registrar, nunca emitir (tarefa D4). Só contas e formatos,
 * sem tela; testado em testes/notas.mjs.
 *
 * Ninguém digita os 44 números da chave. A nota entra por um destes caminhos,
 * do mais completo ao mais simples:
 *   - XML da NF-e: tudo (chave, quem emitiu, para quem, data, valor, itens);
 *   - código de barras da DANFE (câmera): a chave;
 *   - QR do cupom (NFC-e, câmera): a chave e o endereço de consulta;
 *   - PDF da nota: a chave, lida do texto do arquivo (quando tem texto);
 *   - foto ou print: nada automático; valor e data à mão.
 *
 * A chave é conferida pelo dígito verificador (módulo 11). O mesmo cálculo
 * está no servidor (servidor/notas.ts) e no banco (009 › chave_nfe_valida).
 */

/* ---------------------------------------------------------------------------
 * Tipos
 * ------------------------------------------------------------------------- */

export type Sentido = 'entrada' | 'saida'
export type OrigemDaNota = 'xml' | 'barras' | 'qr' | 'pdf' | 'foto'
/** A que a nota se liga: a ficha, a despesa do extrato ou o movimento da catira (venda ou recria). */
export type TipoDeLigacao = 'ficha' | 'despesa' | 'catira'
export interface Ligacao { tipo: TipoDeLigacao; id: string }

export interface ItemDaNota {
  descricao: string
  quantidade: number | null
  unidade: string
  valor: number | null
}

export interface Nota {
  id: string
  /** 44 números, ou null (foto sem leitura, PDF sem texto) */
  chave: string | null
  origem: OrigemDaNota
  sentido: Sentido
  /** data de emissão, «2026-09-15» */
  data: string
  valor: number
  numero: string
  serie: string
  emitenteDoc: string
  emitenteNome: string
  destinatarioDoc: string
  destinatarioNome: string
  descricao: string
  itens: ItemDaNota[]
  /** do QR do cupom: onde a SEFAZ mostra a nota */
  urlConsulta: string
  /** o arquivo guardado (XML, PDF ou foto): mesmo id no aparelho, no banco e no R2 */
  arquivoId: string | null
  /** nome e tipo do arquivo, para mostrar (só no aparelho de quem anexou) */
  arquivo?: { nome: string; tipo: string; tamanho: number } | null
  ligacao: Ligacao | null
  lancadoPor: string
  lancadoPorNome: string
  lancadoEm: string
  anulacao: { em: string; por: string; motivo: string } | null
  /** o servidor recusou de vez: só existe neste aparelho, riscada e fora das listas */
  recusa?: { em: string; motivo: string } | null
}

/** O que a leitura (XML, câmera, PDF) conseguiu tirar da nota — vai pré-preenchido no formulário. */
export interface LeituraDaNota {
  origem: OrigemDaNota
  chave: string | null
  data?: string
  valor?: number
  numero?: string
  serie?: string
  emitenteDoc?: string
  emitenteNome?: string
  destinatarioDoc?: string
  destinatarioNome?: string
  itens?: ItemDaNota[]
  urlConsulta?: string
  /** tpNF do XML: «0» entrada, «1» saída — do ponto de vista de quem EMITIU */
  tipoNoXml?: '0' | '1'
}

export const NOME_DA_ORIGEM: Record<OrigemDaNota, string> = {
  xml: 'XML', barras: 'código de barras', qr: 'QR do cupom', pdf: 'PDF', foto: 'foto',
}

/* ---------------------------------------------------------------------------
 * Chave de acesso
 * ------------------------------------------------------------------------- */

export function somenteDigitos(s: string | null | undefined): string {
  return String(s ?? '').replace(/\D/g, '')
}

/** Dígito verificador dos 43 primeiros números: módulo 11, pesos 2 a 9 da direita para a esquerda. */
export function digitoDaChave(c43: string): number {
  let soma = 0
  let peso = 2
  for (let i = c43.length - 1; i >= 0; i--) {
    soma += Number(c43[i]) * peso
    peso = peso === 9 ? 2 : peso + 1
  }
  const dv = 11 - (soma % 11)
  return dv >= 10 ? 0 : dv
}

/** 44 números com o último batendo com o cálculo. Aceita espaços e pontos no meio. */
export function chaveValida(chave: string | null | undefined): boolean {
  const c = somenteDigitos(chave)
  // uma chave de verdade nunca tem a mesma marca em todos os números (00…0 passaria no cálculo)
  return c.length === 44 && !/^(\d)\1+$/.test(c) && digitoDaChave(c.slice(0, 43)) === Number(c[43])
}

/** «3526 0912 3456 …» — em blocos de 4, como a DANFE imprime. */
export function formatarChave(chave: string | null | undefined): string {
  return somenteDigitos(chave).replace(/(\d{4})(?=\d)/g, '$1 ')
}

/** O que a própria chave conta: estado, ano e mês, CNPJ de quem emitiu, modelo, série e número. */
export interface InfoDaChave {
  uf: string
  /** «2026-09» */
  anoMes: string
  emitenteDoc: string
  /** 55 = NF-e (DANFE), 65 = NFC-e (cupom) */
  modelo: string
  serie: string
  numero: string
}

export function infoDaChave(chave: string): InfoDaChave | null {
  const c = somenteDigitos(chave)
  if (!chaveValida(c)) return null
  return {
    uf: c.slice(0, 2),
    anoMes: `20${c.slice(2, 4)}-${c.slice(4, 6)}`,
    emitenteDoc: c.slice(6, 20),
    modelo: c.slice(20, 22),
    serie: String(Number(c.slice(22, 25))),
    numero: String(Number(c.slice(25, 34))),
  }
}

export function nomeDoModelo(chave: string | null): string {
  const m = chave ? somenteDigitos(chave).slice(20, 22) : ''
  return m === '65' ? 'NFC-e (cupom)' : m === '55' ? 'NF-e' : 'Nota'
}

/**
 * Acha a chave dentro de um texto qualquer (texto do PDF, código lido). Aceita
 * a chave colada ou em blocos separados por espaço ou ponto. Só aceita trecho
 * com EXATAMENTE 44 números que passe no dígito verificador — número maior
 * colado (ou pedaço de outro número) não vira chave por acaso.
 */
export function acharChave(texto: string): string | null {
  const s = String(texto ?? '')
  // trechos de números com espaço ou ponto no meio
  for (const trecho of s.match(/\d[\d \t\r\n.]*\d/g) ?? []) {
    const pedacos = trecho.split(/[\s.]+/).filter(Boolean)
    // qualquer sequência de pedaços vizinhos que some 44 números
    for (let i = 0; i < pedacos.length; i++) {
      let junto = ''
      for (let j = i; j < pedacos.length && junto.length < 44; j++) {
        junto += pedacos[j]
        if (junto.length === 44 && chaveValida(junto)) return junto
      }
    }
  }
  return null
}

/* ---------------------------------------------------------------------------
 * Código de barras da DANFE e QR do cupom (NFC-e)
 * ------------------------------------------------------------------------- */

/**
 * O que a câmera leu. Código de barras da DANFE (code_128): os 44 números.
 * QR do cupom (NFC-e): um endereço da SEFAZ com `p=<chave>|…` (ou, nos mais
 * antigos, `chNFe=<chave>`). No QR de cupom emitido sem sinal (contingência),
 * o `p=` traz também o dia e o valor: aproveitados.
 */
export function lerCodigo(texto: string): LeituraDaNota | null {
  const t = String(texto ?? '').trim()
  const soNumeros = somenteDigitos(t)
  if (/^[\d\s]+$/.test(t) && soNumeros.length === 44) {
    return chaveValida(soNumeros) ? { origem: 'barras', chave: soNumeros } : null
  }
  return lerQrDoCupom(t)
}

export function lerQrDoCupom(texto: string): LeituraDaNota | null {
  const t = String(texto ?? '').trim()
  let p = ''
  const mp = /[?&]p=([^&#\s]+)/i.exec(t)
  if (mp) {
    try { p = decodeURIComponent(mp[1]!) } catch { p = mp[1]! }
  } else {
    const mc = /[?&]chNFe=(\d{44})/i.exec(t)
    if (mc) p = mc[1]!
  }
  if (!p) return null
  const chave = somenteDigitos(p).slice(0, 44)
  if (!chaveValida(chave)) return null
  const url = /^https?:\/\//i.test(t) ? t.slice(0, 600) : ''
  const leitura: LeituraDaNota = { origem: 'qr', chave, urlConsulta: url }
  // contingência: chave|versão|ambiente|dia|valor|digVal|token|hash
  const partes = p.split('|')
  if (partes.length >= 8) {
    const dia = Number(partes[3])
    const valor = Number(partes[4])
    const info = infoDaChave(chave)
    if (info && dia >= 1 && dia <= 31) leitura.data = `${info.anoMes}-${String(dia).padStart(2, '0')}`
    if (Number.isFinite(valor) && valor > 0) leitura.valor = Math.round(valor * 100) / 100
  }
  return leitura
}

/* ---------------------------------------------------------------------------
 * XML da NF-e (nfeProc/NFe/infNFe), lido pelo DOMParser do navegador
 * ------------------------------------------------------------------------- */

/**
 * O mínimo de um elemento do DOM que a leitura usa. No navegador é o
 * resultado do DOMParser; no teste, um leitor pequeno que devolve o mesmo
 * formato (o Node não tem DOMParser).
 */
export interface ElementoXml {
  localName: string
  children: ArrayLike<ElementoXml>
  textContent: string | null
  getAttribute(nome: string): string | null
}
export interface DocumentoXml {
  documentElement: ElementoXml | null
  getElementsByTagName?(nome: string): ArrayLike<unknown>
}
export type Analisador = (texto: string) => DocumentoXml

const analisarNoNavegador: Analisador = (texto) =>
  new DOMParser().parseFromString(texto, 'application/xml') as unknown as DocumentoXml

/** Arquivo que não é XML de nota: a mensagem é a frase para a tela. */
export class XmlRecusado extends Error {}

function filhos(el: ElementoXml | null | undefined, nome: string): ElementoXml[] {
  if (!el) return []
  return Array.from(el.children).filter((c) => c.localName === nome)
}
function filho(el: ElementoXml | null | undefined, ...caminho: string[]): ElementoXml | null {
  let atual: ElementoXml | null = el ?? null
  for (const nome of caminho) atual = filhos(atual, nome)[0] ?? null
  return atual
}
function texto(el: ElementoXml | null | undefined, ...caminho: string[]): string {
  return (filho(el, ...caminho)?.textContent ?? '').trim()
}
/** Busca em profundidade: o infNFe pode vir dentro de nfeProc/NFe ou direto em NFe. */
function procurar(el: ElementoXml | null, nome: string): ElementoXml | null {
  if (!el) return null
  if (el.localName === nome) return el
  for (const c of Array.from(el.children)) {
    const achou = procurar(c, nome)
    if (achou) return achou
  }
  return null
}
function numeroXml(s: string): number | null {
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/**
 * Lê o XML da NF-e (ou NFC-e): chave, emitente, destinatário, data, valor
 * total e itens. Lança `XmlRecusado` com a frase quando não é XML de nota.
 */
export function lerXmlDaNota(conteudo: string, analisar: Analisador = analisarNoNavegador): LeituraDaNota {
  let doc: DocumentoXml
  try {
    doc = analisar(String(conteudo ?? '').replace(/^﻿/, ''))
  } catch {
    throw new XmlRecusado('Não deu para ler este arquivo. Confira se é o XML da nota.')
  }
  const raiz = doc.documentElement
  if (!raiz || raiz.localName === 'parsererror' || (doc.getElementsByTagName?.('parsererror').length ?? 0) > 0) {
    throw new XmlRecusado('Este arquivo não é um XML válido. Peça de novo o XML da nota a quem emitiu.')
  }
  const inf = procurar(raiz, 'infNFe')
  if (!inf) throw new XmlRecusado('Este XML não é de nota fiscal (NF-e ou NFC-e). Confira o arquivo.')

  const chaveId = somenteDigitos(inf.getAttribute('Id'))
  const chaveProt = somenteDigitos(texto(procurar(raiz, 'infProt'), 'chNFe'))
  const chave = chaveValida(chaveId) ? chaveId : chaveValida(chaveProt) ? chaveProt : null
  if (!chave) throw new XmlRecusado('A chave de acesso deste XML não confere. O arquivo pode estar incompleto ou alterado.')

  const ide = filho(inf, 'ide')
  const emit = filho(inf, 'emit')
  const dest = filho(inf, 'dest')
  const quando = texto(ide, 'dhEmi') || texto(ide, 'dEmi')
  const total = numeroXml(texto(inf, 'total', 'ICMSTot', 'vNF'))
  const tp = texto(ide, 'tpNF')

  const itens: ItemDaNota[] = filhos(inf, 'det').map((det) => {
    const prod = filho(det, 'prod')
    return {
      descricao: texto(prod, 'xProd').slice(0, 120),
      quantidade: numeroXml(texto(prod, 'qCom')),
      unidade: texto(prod, 'uCom').slice(0, 10),
      valor: numeroXml(texto(prod, 'vProd')),
    }
  })

  const leitura: LeituraDaNota = {
    origem: 'xml',
    chave,
    numero: texto(ide, 'nNF'),
    serie: texto(ide, 'serie'),
    emitenteDoc: texto(emit, 'CNPJ') || texto(emit, 'CPF'),
    emitenteNome: texto(emit, 'xNome'),
    destinatarioDoc: texto(dest, 'CNPJ') || texto(dest, 'CPF'),
    destinatarioNome: texto(dest, 'xNome'),
    itens: itens.slice(0, 200),
  }
  // «2026-09-15T10:20:00-03:00»: o dia como está escrito na nota (sem trocar fuso)
  if (/^\d{4}-\d{2}-\d{2}/.test(quando)) leitura.data = quando.slice(0, 10)
  if (total !== null) leitura.valor = Math.round(total * 100) / 100
  if (tp === '0' || tp === '1') leitura.tipoNoXml = tp
  return leitura
}

/**
 * Entrada ou saída, do ponto de vista da EMPRESA que usa o app.
 *   - quem emitiu foi a empresa: vale o tpNF (0 entrada, 1 saída);
 *   - outro emitiu: o contrário (a saída dele é a nossa entrada).
 * Sem tpNF (foto, câmera): entrada, que é o mais comum (despesa, compra).
 * A pessoa sempre pode trocar no formulário.
 */
export function sentidoSugerido(l: Pick<LeituraDaNota, 'emitenteDoc' | 'tipoNoXml'>, docDaEmpresa: string): Sentido {
  const nosso = somenteDigitos(docDaEmpresa)
  const emitiuNos = !!nosso && somenteDigitos(l.emitenteDoc) === nosso
  if (!l.tipoNoXml) return 'entrada'
  if (emitiuNos) return l.tipoNoXml === '0' ? 'entrada' : 'saida'
  return l.tipoNoXml === '0' ? 'saida' : 'entrada'
}

/* ---------------------------------------------------------------------------
 * PDF da nota — sem biblioteca: procura os 44 números no texto do arquivo
 * ------------------------------------------------------------------------- */

/** Abre um trecho comprimido (FlateDecode). No navegador e no Node, pelo DecompressionStream. */
export type Inflador = (b: Uint8Array) => Promise<Uint8Array>

const LIMITE_INFLADO = 16 * 1024 * 1024

export const inflarComDecompressionStream: Inflador = async (b) => {
  const fluxo = new Blob([b as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'))
  const leitor = fluxo.getReader()
  const partes: Uint8Array[] = []
  let total = 0
  for (;;) {
    const { done, value } = await leitor.read()
    if (done) break
    partes.push(value)
    total += value.length
    // PDF malicioso que «explode» ao abrir: para no limite
    if (total > LIMITE_INFLADO) { await leitor.cancel(); break }
  }
  const saida = new Uint8Array(total)
  let pos = 0
  for (const p of partes) { saida.set(p, pos); pos += p.length }
  return saida
}

function latin1(b: Uint8Array): string {
  let s = ''
  for (let i = 0; i < b.length; i += 8192) s += String.fromCharCode(...b.subarray(i, i + 8192))
  return s
}

/** As cadeias de texto de um trecho de página: «(3526 0912) Tj», «[(35)-20(26)] TJ», «<33353236> Tj». */
function textosDaPagina(s: string): string {
  const saida: string[] = []
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === '(') {
      let nivel = 1
      let t = ''
      for (i++; i < s.length && nivel > 0; i++) {
        const d = s[i]!
        if (d === '\\') {
          const e = s[++i] ?? ''
          if (/[0-7]/.test(e)) {
            let oct = e
            while (oct.length < 3 && /[0-7]/.test(s[i + 1] ?? '')) oct += s[++i]
            t += String.fromCharCode(parseInt(oct, 8))
          } else t += e === 'n' ? '\n' : e === 'r' ? '\r' : e === 't' ? '\t' : e
          continue
        }
        if (d === '(') nivel++
        if (d === ')') { nivel--; if (!nivel) break }
        t += d
      }
      saida.push(t)
    } else if (c === '<' && s[i + 1] !== '<') {
      const fim = s.indexOf('>', i)
      if (fim < 0) break
      const hex = s.slice(i + 1, fim).replace(/\s/g, '')
      // só cadeias de 1 byte por letra viram texto; fonte de 2 bytes não se lê sem a tabela da fonte
      if (/^[0-9a-fA-F]*$/.test(hex) && hex.length % 2 === 0) {
        let t = ''
        for (let k = 0; k < hex.length; k += 2) t += String.fromCharCode(parseInt(hex.slice(k, k + 2), 16))
        saida.push(t)
      }
      i = fim
    }
  }
  return saida.join(' ')
}

/**
 * Procura a chave de acesso dentro do PDF: no texto solto do arquivo e no
 * texto de cada trecho comprimido (FlateDecode), aberto com o inflador. Se o
 * PDF for só imagem (nota escaneada) ou usar fonte que não se lê sem tabela,
 * devolve null: o arquivo fica guardado e o valor é digitado.
 */
export async function chaveDoPdf(bytes: Uint8Array, inflar: Inflador = inflarComDecompressionStream): Promise<string | null> {
  const bruto = latin1(bytes)
  const direto = acharChave(bruto) ?? acharChave(textosDaPagina(bruto))
  if (direto) return direto
  // «stream» que abre um trecho (e não o fim de outro, «endstream»)
  const marca = /(?<!end)stream\r?\n/g
  let m: RegExpExecArray | null
  while ((m = marca.exec(bruto))) {
    const inicio = m.index + m[0].length
    // o dicionário do trecho fica entre o último «obj» e a palavra «stream»
    const dicionario = bruto.slice(Math.max(0, bruto.lastIndexOf('obj', m.index)), m.index)
    const fim = bruto.indexOf('endstream', inicio)
    if (fim < 0) break
    marca.lastIndex = fim
    if (!/\/FlateDecode/.test(dicionario) || /\/Subtype\s*\/Image/.test(dicionario)) continue
    // usa o /Length quando é número direto; senão, corta a quebra de linha antes do «endstream»
    const comprimento = /\/Length\s+(\d+)(?!\s+\d+\s+R)/.exec(dicionario)
    let ate = comprimento ? inicio + Number(comprimento[1]) : fim
    if (ate > fim) ate = fim
    while (ate > inicio && (bruto[ate - 1] === '\n' || bruto[ate - 1] === '\r')) ate--
    try {
      const aberto = latin1(await inflar(bytes.subarray(inicio, ate)))
      const achou = acharChave(textosDaPagina(aberto)) ?? acharChave(aberto)
      if (achou) return achou
    } catch {
      // trecho estragado ou de outro formato: segue para o próximo
    }
  }
  return null
}

/* ---------------------------------------------------------------------------
 * A lista do período — base do relatório de receitas e despesas (P3)
 * ------------------------------------------------------------------------- */

/** Nota que conta: não anulada e não recusada pelo servidor. */
export function notaValendo(n: Pick<Nota, 'anulacao' | 'recusa'>): boolean {
  return !n.anulacao && !n.recusa
}

export interface LinhaDoPeriodo {
  id: string
  data: string
  sentido: Sentido
  descricao: string
  valor: number
  /** «NF-e 123 série 1», «NFC-e (cupom) 456», «Foto» */
  documento: string
  chave: string | null
  ligacao: Ligacao | null
  /** «Ficha C1-2026-0003», «Despesa · Almoço 02/10», «Catira · venda 05/10» — dado por quem chama */
  ligadoA: string
}

/** O que a nota É, em poucas palavras: a descrição da pessoa, ou com quem foi, ou o primeiro item. */
export function descricaoDaNota(n: Pick<Nota, 'descricao' | 'sentido' | 'emitenteNome' | 'destinatarioNome' | 'itens'>): string {
  const outraParte = n.sentido === 'entrada' ? n.emitenteNome : n.destinatarioNome || n.emitenteNome
  return n.descricao || outraParte || n.itens[0]?.descricao || ''
}

export function documentoDaNota(n: Pick<Nota, 'chave' | 'numero' | 'serie' | 'origem'>): string {
  if (!n.chave) return n.origem === 'pdf' ? 'PDF sem chave' : 'Foto'
  const info = infoDaChave(n.chave)
  const numero = n.numero || info?.numero || ''
  const serie = n.serie || info?.serie || ''
  return `${nomeDoModelo(n.chave)}${numero ? ` ${numero}` : ''}${serie ? ` série ${serie}` : ''}`
}

/**
 * As notas que valem, com data entre `de` e `ate` (inclusive), em ordem de
 * data: data, descrição, valor, documento e a ligação. `rotular` diz o nome
 * da ligação (a ficha, a despesa, o movimento) — quem chama conhece.
 */
export function notasDoPeriodo(
  notas: Nota[], de: string, ate: string,
  filtro: { sentido?: Sentido | null; semLigacao?: boolean } = {},
  rotular: (l: Ligacao) => string = (l) => `${l.tipo} ${l.id}`,
): LinhaDoPeriodo[] {
  return notas
    .filter((n) => notaValendo(n) && n.data >= de && n.data <= ate
      && (!filtro.sentido || n.sentido === filtro.sentido)
      && (!filtro.semLigacao || !n.ligacao))
    .sort((a, b) => a.data.localeCompare(b.data) || a.lancadoEm.localeCompare(b.lancadoEm))
    .map((n) => ({
      id: n.id, data: n.data, sentido: n.sentido, descricao: descricaoDaNota(n), valor: n.valor,
      documento: documentoDaNota(n), chave: n.chave, ligacao: n.ligacao, ligadoA: n.ligacao ? rotular(n.ligacao) : '',
    }))
}

export interface TotaisDoPeriodo { entradas: number; saidas: number; quantas: number }

export function totaisDoPeriodo(linhas: Pick<LinhaDoPeriodo, 'sentido' | 'valor'>[]): TotaisDoPeriodo {
  let entradas = 0
  let saidas = 0
  for (const l of linhas) {
    if (l.sentido === 'entrada') entradas += l.valor
    else saidas += l.valor
  }
  return { entradas: Math.round(entradas * 100) / 100, saidas: Math.round(saidas * 100) / 100, quantas: linhas.length }
}

/** A nota com a mesma chave que já vale (neste aparelho): a mesma nota não entra duas vezes. */
export function notaComAChave(notas: Nota[], chave: string | null): Nota | undefined {
  if (!chave) return undefined
  return notas.find((n) => n.chave === chave && notaValendo(n))
}
